import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  CardFooter,
  Form,
  FormGroup,
  Input,
  Row,
  Col,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const SliderNewsCMS = () => {
  const [imageNews, setImageNews] = useState([]);
  const [textNews, setTextNews] = useState([]);
  const [newImage, setNewImage] = useState(null);
  const [newText, setNewText] = useState('');
  const [addSuccessMessage, setAddSuccessMessage] = useState('');
  const [addError, setAddError] = useState('');
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [newsItemToDelete, setNewsItemToDelete] = useState(null);
  const [editingImageNews, setEditingImageNews] = useState(null);
  const [editingTextNews, setEditingTextNews] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    fetchNewsImages();
    fetchNewsTexts();
  }, []);

  const fetchNewsImages = async () => {
    try {
      const response = await axios.get('https://back.zubilaw.com/SliderNews/listImages');
      setImageNews(response.data.data);
    } catch (error) {
      console.error('Error fetching news images:', error);
    }
  };

  const fetchNewsTexts = async () => {
    try {
      const response = await axios.get('https://back.zubilaw.com/SliderNews/listTexts');
      setTextNews(response.data.data);
    } catch (error) {
      console.error('Error fetching news texts:', error);
    }
  };

  const isValidImageExtension = (fileName) => {
    const validExtensions = ['.webp', '.gif', '.png', '.jpg', '.jpeg'];
    const fileExtension = fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase();
    return validExtensions.includes(`.${fileExtension}`);
  };

  const addNewsImage = async () => {
    const errors = {};
    if (!newImage) {
      errors.image = 'Please select an image to upload.';
    } else if (!isValidImageExtension(newImage.name)) {
      errors.image = 'Invalid image file extension. Allowed extensions are .webp, .gif, .png, .jpg, .jpeg.';
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const formData = new FormData();
    formData.append('slider', newImage);

    try {
      await axios.post('https://back.zubilaw.com/SliderNews/addImage', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAddSuccessMessage('News image added successfully!');
      setTimeout(() => {
        setAddSuccessMessage('');
      }, 5000);
      setNewImage(null);
      fetchNewsImages();
    } catch (error) {
      setAddError('Error adding news image.');
      setTimeout(() => {
        setAddError('');
      }, 5000);
      console.error('Error adding news image:', error);
    }
  };

  const addNewsText = async () => {
    const data = {
      text: newText,
    };

    try {
      await axios.post('https://back.zubilaw.com/SliderNews/addText', data, {
        headers: { 'Content-Type': 'application/json' },
      });
      setAddSuccessMessage('News text added successfully!');
      setTimeout(() => {
        setAddSuccessMessage('');
      }, 5000);
      setNewText('');
      fetchNewsTexts();
    } catch (error) {
      setAddError('Please enter text to add.');
      console.error('Error adding news text:', error);
    }
  };

  const updateNewsImage = async (id) => {
    const errors = {};
    if (!newImage) {
      errors.image = 'Please select an image to upload.';
    } else if (!isValidImageExtension(newImage.name)) {
      errors.image = 'Invalid image file extension. Allowed extensions are .webp, .gif, .png, .jpg, .jpeg.';
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const formData = new FormData();
    formData.append('slider', newImage);

    try {
      await axios.put(`https://back.zubilaw.com/SliderNews/updateImage/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUpdateSuccessMessage('News image updated successfully!');
      setEditingImageNews(null);
      setNewImage(null);
      fetchNewsImages();
    } catch (error) {
      setUpdateError('Error updating news image.');
      console.error('Error updating news image:', error);
    }
  };

  const updateNewsText = async (id) => {
    const data = {
      text: newText,
    };

    try {
      await axios.put(`https://back.zubilaw.com/SliderNews/updateText/${id}`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      setUpdateSuccessMessage('News text updated successfully!');
      setEditingTextNews(null);
      setNewText('');
      fetchNewsTexts();
    } catch (error) {
      setUpdateError('Error updating news text.');
      console.error('Error updating news text:', error);
    }
  };

  const deleteNews = (id) => {
    setNewsItemToDelete(id);
    setShowDeletePopup(true);
  };

  const performDelete = async () => {
    try {
      await axios.delete(`https://back.zubilaw.com/SliderNews/delete/${newsItemToDelete}`);
      setShowDeletePopup(false);
      fetchNewsImages();
      fetchNewsTexts();
    } catch (error) {
      console.error('Error deleting news item:', error);
    }
  };

  const closeUpdateForm = () => {
    setEditingImageNews(null);
    setEditingTextNews(null);
    setNewImage(null);
    setNewText('');
  };

  const modules = {
    toolbar: [
      [{ 'color': [] }, { 'background': [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'header': '1' }, { 'header': '2' }, { 'header': [3, 4, 5, 6] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']                                         
    ],
  };
  
  const formats = [
    'background', 'color', 'bold', 'italic', 'underline', 'strike', 'header',
    'list', 'bullet', 'align', 'link', 'image', 'video'
  ];

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Manage News Images</CardTitle>
            </CardHeader>
            <CardBody>
              <Form>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>Image</label>
                      <Input
                        type="file"
                        onChange={(e) => setNewImage(e.target.files[0])}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <Button
                      className="btn-round"
                      color="primary"
                      onClick={addNewsImage}
                    >
                      Add News Image
                    </Button>
                    {addSuccessMessage && (
                      <div className="alert alert-success" role="alert" style={{ width: '50%', margin: '10px auto', textAlign: 'center' }}>
                        {addSuccessMessage}
                      </div>
                    )}
                    {addError && (
                      <div style={{ color: "red", marginTop: "5px" }} role="alert">
                        {addError}
                      </div>
                    )}
                    {fieldErrors.image && <div style={{ color: "red", marginTop: "5px" }}>{fieldErrors.image}</div>}
                  </Col>
                </Row>
              </Form>
              <Table responsive>
  <thead className="text-primary">
    <tr>
      <th>Image</th>
      <th>Actions</th>
    </tr>
  </thead>
  <tbody>
    {imageNews.map((newsItem, index) => (
      <tr key={newsItem.id}>
        <td>
          <img
            src={`https://back.zubilaw.com/${newsItem.slider}`}
            alt="News Slider"
            style={{ maxWidth: '300px', maxHeight: '200px' }}
          />
        </td>
        <td>
          <Button
            color="success"
            onClick={() => {
              setEditingImageNews(newsItem);
              setNewImage(null);
            }}
          >
            Update Image
          </Button>{" "}
          {index !== 0 && (
            <Button
              color="danger"
              onClick={() => deleteNews(newsItem.id)}
            >
              Delete
            </Button>
          )}
        </td>
      </tr>
    ))}
  </tbody>
</Table>

            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Text Details</CardTitle>
            </CardHeader>
            <CardBody>
              {/* <Form>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <label>Text</label>
                      <Input
                        type="text"
                        value={newText}
                        onChange={(e) => setNewText(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <Button
                      className="btn-round"
                      color="primary"
                      onClick={addNewsText}
                    >
                      Add News Text
                    </Button>
                    {addSuccessMessage && (
                      <div className="alert alert-success" role="alert" style={{ width: '50%', margin: '10px auto', textAlign: 'center' }}>
                        {addSuccessMessage}
                      </div>
                    )}
                    {addError && (
                      <div style={{ color: "red", marginTop: "5px" }} role="alert">
                        {addError}
                      </div>
                    )}
                  </Col>
                </Row>
              </Form> */}
              <Table responsive>
                <thead className="text-primary">
                  <tr>
                    <th>Text</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {textNews.map((newsItem) => (
                    <tr key={newsItem.id}>
                      <td dangerouslySetInnerHTML={{ __html: newsItem.text }}></td>
                      <td>
                        <Button
                          color="success"
                          onClick={() => {
                            setEditingTextNews(newsItem);
                            setNewText(newsItem.text);
                          }}
                        >
                          Update Text
                        </Button>{" "}
                        {/* <Button
                          color="danger"
                          onClick={() => deleteNews(newsItem.id)}
                        >
                          Delete
                        </Button> */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {(editingImageNews || editingTextNews) && (
       <div className="content">
          <Form>
            <Row>
              {editingTextNews && (
                <Col md="7">
                  <FormGroup>
                    <label>Text</label>
                    <div
                      style={{
                        height: '300px',
                        overflowY: 'auto',
                      }}
                    >
                      <ReactQuill
                        value={newText}
                        onChange={setNewText}
                        modules={modules} 
                        formats={formats}
                        style={{ height: '80%', color: 'black' }}
                      />
                    </div>
                  </FormGroup>
                </Col>
              )}

              {editingImageNews && (
                <Col md="6">
                  <FormGroup>
                    <label>Image</label>
                    <Input
                      type="file"
                      onChange={(e) => setNewImage(e.target.files[0])}
                    />
                    {fieldErrors.image && <div style={{ color: "red", marginTop: "5px" }}>{fieldErrors.image}</div>}
                  </FormGroup>
                </Col>
              )}
              <Col md="12">
                {editingTextNews && (
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => updateNewsText(editingTextNews.id)}
                  >
                    Update Text
                  </Button>
                )}{" "}
                {editingImageNews && (
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => updateNewsImage(editingImageNews.id)}
                  >
                    Update Image
                  </Button>
                )}{" "}
                <Button
                  className="btn-round"
                  color="secondary"
                  onClick={closeUpdateForm}
                >
                  Cancel
                </Button>
                {updateSuccessMessage && (
                  <div className="alert alert-success" role="alert" style={{ width: '50%', margin: '10px auto', textAlign: 'center' }}>
                    {updateSuccessMessage}
                  </div>
                )}
                {updateError && (
                  <div className="alert alert-danger" role="alert" style={{ width: '50%', margin: '10px auto', textAlign: 'center' }}>
                    {updateError}
                  </div>
                )}
              </Col>
            </Row>
          </Form>
          </div>
      )}

      <Modal isOpen={showDeletePopup} toggle={() => setShowDeletePopup(false)}>
        <ModalHeader toggle={() => setShowDeletePopup(false)}>Delete News Item</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this news item?
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={performDelete}>Delete</Button>
          <Button color="secondary" onClick={() => setShowDeletePopup(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SliderNewsCMS;
